import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { fetchWordDefinition } from '../../API/DictionaryApi';
import LoadingIcon from '../../Components/LoadingIcon';
import ReadAloudIcon from '../../Components/ReadAloudIcon';
import TextToSpeech from '../../Components/TextToSpeech';
import { AudioPlayerState, useAudio } from '../../Context/AudioContext';
import { WordDefinition } from '../../Types/WordDefinition';
import './Dictionary.css';

interface DictionaryProps {
  word?: string;
  context?: string;
  articleLexileLevel?: string;
  onClose?: () => void;
  onError?: (error: Error) => void;
  onCancel?: () => void;
}

const Dictionary = forwardRef<HTMLDivElement, DictionaryProps>(
  ({ word, context, articleLexileLevel, onClose, onError, onCancel }) => {
    const ref = useRef<HTMLDivElement>(null);
    const { audioManager } = useAudio();
    const [isLoading, setIsLoading] = useState(false);
    const [definition, setDefinition] = useState<WordDefinition | null>(null);
    const [baseWord, setBaseWord] = useState<string | null>(null);
    const [playbackState, setPlaybackState] = useState<AudioPlayerState>(
      AudioPlayerState.Stop,
    );
    const [isTTSAudioLoaded, setIsTTSAudioLoaded] = useState(false);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent | TouchEvent) => {
        if (
          ref &&
          'current' in ref &&
          ref.current &&
          !ref.current.contains(event.target as Node)
        ) {
          onClose?.();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }, [onClose]);

    useEffect(() => {
      if (word && context) {
        setIsLoading(true);
        setDefinition(null);

        fetchWordDefinition(word, context, articleLexileLevel)
          .then((response) => {
            setDefinition(response);
            setBaseWord(response.word);
          })
          .catch((error) => {
            setDefinition(null);
            if (error.name === 'CanceledError') {
              onCancel?.();
            } else {
              onError?.(error);
              toast.error('Failed to look up word definition');
            }
          })
          .finally(() => {
            setIsLoading(false);
          });
      } else {
        setIsLoading(false);
        setDefinition(null);
      }
    }, [word, context, articleLexileLevel]);

    if (!word) {
      return null;
    }

    if (!isLoading && !definition) {
      return null;
    }

    const shouldFlipOrientation = () => {
      if (ref && 'current' in ref && ref.current) {
        const parentElement = ref.current.parentElement;
        if (parentElement) {
          const rect = parentElement.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const isNarrowScreen = viewportWidth <= 640;
          const isRightHalf = rect.left > viewportWidth / 2;
          return isNarrowScreen && isRightHalf;
        }
      }
      return false;
    };

    const playReadAloud = () => {
      setPlaybackState(AudioPlayerState.Play);
      setIsTTSAudioLoaded(false);
    };

    const stopReadAloud = () => {
      setPlaybackState(AudioPlayerState.Stop);
    };

    return (
      <div
        ref={ref}
        className={`dictionary-element absolute z-50 max-sm:max-w-[50vw] max-w-[36vw] ${
          isLoading ? 'h-auto w-auto' : ''
        } ${
          shouldFlipOrientation()
            ? 'flip-pointer right-0 translate-x-3'
            : 'left-0 -translate-x-3'
        } bottom-full -translate-y-2`}
      >
        {isLoading ? (
          <div className="flex justify-center items-center p-2">
            <LoadingIcon size={4} />
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-2 text-sm">
              <h5 className="font-bold">Definition</h5>
              <div className="flex gap-1">
                <ReadAloudIcon
                  playbackState={playbackState}
                  isPlaybackLoaded={isTTSAudioLoaded}
                  onPlayClick={playReadAloud}
                  onStopClick={stopReadAloud}
                  buttonClassName="p-1 rounded cursor-pointer"
                />
              </div>
            </div>
            <TextToSpeech
              key={`dictionary-${word}`}
              descriptorUrl={`${window.env.REACT_APP_API_URL}/tts/dictionary?word=${encodeURIComponent(word)}`}
              manager={audioManager}
              state={playbackState}
              preload={false}
              analyticsContext={{
                type: 'dictionary',
                word: word,
              }}
              onEnd={stopReadAloud}
              onPlay={() => setIsTTSAudioLoaded(true)}
            />
            {definition && definition.meanings.length > 0 ? (
              definition.meanings.map((meaning, index) => (
                <div key={index} className="text-left mb-2 text-sm">
                  <p>
                    <span className="font-bold">{baseWord || word} | </span>
                    <span className="font-bold">{meaning.partOfSpeech}</span>
                  </p>
                  <ul>
                    {meaning.definitions.map((d, defIndex) => (
                      <li key={defIndex}>{d.definition}</li>
                    ))}
                  </ul>
                </div>
              ))
            ) : (
              <div className="text-left mb-2">
                <p>
                  No definition found for{' '}
                  <span className="font-bold">{word}</span>.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    );
  },
);

export default Dictionary;
